import React, { useEffect, useState } from 'react';
import { ErrorRounded } from '@mui/icons-material';
import CancelIcon from '@mui/icons-material/Cancel';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import ReplayIcon from '@mui/icons-material/Replay';
import SaveIcon from '@mui/icons-material/Save';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import IntervalTimer from 'react-interval-timer';

import { useApp } from 'contexts/AppContext';
import { useTheme } from 'contexts/ThemeContext';
import { useUser } from 'contexts/UserContext';

import { isValidUrl } from 'utils/functions';
import moment from 'utils/moment';
import pluralize from 'utils/pluralize';
import { ACTIVATION, PML } from 'utils/rest';

import { TableWithColapse, Text } from 'components/DataDisplay';
import { LinearProgress } from 'components/Feedback';
import { Button, Select, TextField } from 'components/Inputs';
import { Container, Item, Row } from 'components/Layout';
import { Loading } from 'components/Loading';
import { Accordion } from 'components/Surfaces';
import { UnauthorizedModal } from 'components/UnauthorizedModal';

import AddShopGroupModal from './components/addShopGroupModal';
import RepiquageImport from './components/RepiquageImport';

const ConfigContainer = () => {
  const { adminRights, profile } = useUser();
  const { domainConfig } = useApp();
  const { themeColors } = useTheme();
  const [original, setOriginal] = useState(null);
  const [googleSheetUrl, setGoogleSheetUrl] = useState('');
  const [googleSheetUrlError, setGoogleSheetUrlError] = useState(false);
  const [shopGroupOptions, setShopGroupOptions] = useState([]);
  const [activationShopGroups, setActivationShopGroups] = useState([]);
  const [config, setConfig] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [computing, setComputing] = useState(false);
  const [abortRequested, setAbortRequested] = useState(false);
  const [openModal, setOpenModal] = useState(null);
  const [setupTask, setSetupTask] = useState({});
  const [configList, setConfigList] = useState([]);
  const [selectedShopGroup, setSelectedShopGroup] = useState('');
  const [warningsExpanded, setWarningExpanded] = useState(false);
  const [rows, setRows] = useState([]);
  const [progress, setProgress] = useState(0);
  const [shopIsInProgress, setShopIsInProgress] = useState(false);
  const [importType, setImportType] = useState('pml');

  const isInProgress = setupTask?.status === 'inProgress';
  const isAborted = setupTask?.status === 'aborted';

  const canImportRepiquage =
    !!domainConfig?.features?.enabledPmlRepiquage &&
    !!profile?.features?.enabledPmlRepiquage;

  const importTypeOptions = [
    { value: 'pml', label: 'Configuration plateforme multi-locale' },
    ...(canImportRepiquage
      ? [
          {
            value: 'repiquage',
            label: 'Import repiquage (campagnes & créas repiquées)'
          }
        ]
      : [])
  ];

  const onExpandChange = () => {
    setWarningExpanded(!warningsExpanded);
  };

  const getShopStatusTxt = status => {
    let effectiveStatus = status;

    if (abortRequested && (status === 'inProgress' || status === 'toDo')) {
      effectiveStatus = 'aborted';
    }

    switch (effectiveStatus) {
      case 'toDo':
        return (
          <Text component="span" fontWeight={400} color="pml">
            Programmé
          </Text>
        );
      case 'inProgress':
        return (
          <Text component="span" fontWeight={400} color="pml">
            En cours
          </Text>
        );
      case 'error':
        return (
          <Text component="span" fontWeight={400} color="error">
            Erreur
          </Text>
        );
      case 'done':
        return (
          <Text component="span" fontWeight={400} color="activation">
            Terminé
          </Text>
        );
      case 'aborted':
        return (
          <Text component="span" fontWeight={400} color="insight">
            Avorté
          </Text>
        );
      default:
        return (
          <Text component="span" fontWeight={400} color="activation">
            {effectiveStatus}
          </Text>
        );
    }
  };

  useEffect(() => {
    setShopGroupOptions([
      { value: 'none', label: 'Aucun' },
      ...activationShopGroups.map(el => ({
        value: el._id,
        label: el.name
      }))
    ]);
  }, [activationShopGroups]);

  useEffect(() => {
    if (googleSheetUrl.length !== 0) {
      setGoogleSheetUrlError(!isValidUrl(googleSheetUrl));
    }
  }, [googleSheetUrl]);

  const getLastTask = async currentConfig => {
    const task = await PML.getLastSetupTask(currentConfig.id);
    const shopKeys = Object.keys(task?.shopStatus || {});

    const rawShopInProgress =
      shopKeys.some(
        key =>
          task?.shopStatus[key]?.status === 'inProgress' ||
          task?.shopStatus[key]?.status === 'toDo'
      ) || false;

    const shopInProgress =
      task?.status === 'aborted' ? false : rawShopInProgress;
    const taskInProgress = task?.status === 'inProgress' || shopInProgress;

    if (!abortRequested) {
      setComputing(taskInProgress);
    } else if (!taskInProgress) {
      setAbortRequested(false);
      setComputing(false);
    }
    setShopIsInProgress(shopInProgress);

    const shopDone = shopKeys.filter(
      key =>
        task?.shopStatus[key]?.status !== 'inProgress' &&
        task?.shopStatus[key]?.status !== 'toDo'
    );

    const finalProgress =
      shopKeys.length > 0 ? (shopDone.length / shopKeys.length) * 100 : 0;
    setProgress(finalProgress);

    const parsedRows = shopKeys.map((el, index) => {
      const oldRow = rows.find(row => row.key === el);
      return {
        key: el,
        index,
        status: getShopStatusTxt(task.shopStatus?.[el]?.status),
        collapse: oldRow?.collapse || false,
        subRows: task?.shopStatus?.[el]?.message ? [task?.shopStatus?.[el]] : []
      };
    });
    setRows(parsedRows);
    setSetupTask(task);
  };

  const getDatas = async () => {
    const [tmpConfigList, tmpActivationShopGroups] = await Promise.all([
      PML.getConfigs('pml'),
      ACTIVATION.getActivationShopGoups()
    ]);

    setActivationShopGroups(tmpActivationShopGroups);

    const indexWithoutGroup = tmpConfigList.findIndex(el => !el.shopGroupId);
    let orderedConfigList;
    if (indexWithoutGroup > 0) {
      const configWithoutGroup = tmpConfigList[indexWithoutGroup];
      orderedConfigList = [
        configWithoutGroup,
        ...tmpConfigList.slice(0, indexWithoutGroup),
        ...tmpConfigList.slice(indexWithoutGroup + 1)
      ];
    } else {
      orderedConfigList = tmpConfigList;
    }

    setConfigList(orderedConfigList);

    setSelectedShopGroup(orderedConfigList[0]?.shopGroupId || 'none');
    setLoaded(true);
  };

  const getStatus = async () => {
    if (config) {
      await getLastTask(config);
    }
  };

  const closeModal = () => {
    setOpenModal(null);
  };

  const onAdd = () => {
    if (
      !adminRights?.pml?.shopGroup?.create ||
      !adminRights?.pml?.config?.create
    ) {
      setOpenModal('unauthorized');
      return;
    }

    setOpenModal('addShopGroup');
  };

  const onSave = async () => {
    if (
      !adminRights?.pml?.config?.update ||
      !adminRights?.pml?.config?.create
    ) {
      setOpenModal('unauthorized');
      return;
    }

    let newConfig;
    if (config._id) {
      newConfig = await PML.updateConfig(config._id, config);
    } else {
      newConfig = await PML.createConfig(config);
    }

    setOriginal(newConfig);
    setConfig(newConfig);
    setConfigList(prev => {
      const newState = [...prev];
      const index = prev.findIndex(el => el._id === newConfig._id);
      if (index !== -1) {
        newState[index] = newConfig;
      } else {
        newState.push(newConfig);
      }

      return newState;
    });
  };

  const onCompute = async (line = '') => {
    if (!adminRights?.pml?.config?.update) {
      setOpenModal('unauthorized');
      return;
    }
    setAbortRequested(false);
    setComputing(true);

    await PML.computeConfig(config.id, line);
    await getStatus();
  };

  useEffect(() => {
    if (!loaded) {
      getDatas();
    }
    // eslint-disable-next-line
  }, [loaded]);

  useEffect(() => {
    let tmpConfig;
    if (selectedShopGroup === 'none') {
      tmpConfig = configList.find(el => !el.shopGroupId);
    } else {
      tmpConfig = configList.find(el => el.shopGroupId === selectedShopGroup);
    }

    const shopGroupId = selectedShopGroup !== 'none' ? selectedShopGroup : '';

    setConfig(tmpConfig || { shopGroupId });
    setOriginal(tmpConfig || { shopGroupId });
    setGoogleSheetUrl(tmpConfig?.googleSheetUrl || '');
    setSetupTask({});
    // eslint-disable-next-line
  }, [selectedShopGroup]);

  const createShopGroup = async datas => {
    const payload = {
      name: datas.name,
      key: datas.key
    };
    if (domainConfig.defaultParentShopGroup) {
      payload.parent = domainConfig.defaultParentShopGroup;
    }
    const created = await ACTIVATION.createActivationShopGoup(payload);
    if (created) {
      await PML.createConfig({
        shopGroupId: created._id,
        googleSheetUrl: datas.googleSheetURL
      });
    }
    setLoaded(false);
    closeModal();
  };

  const getStatusTxt = statusTask => {
    const shopKeys = Object.keys(statusTask.shopStatus || {});
    const inProgress = shopKeys.some(
      key =>
        statusTask?.shopStatus[key]?.status === 'inProgress' ||
        statusTask?.shopStatus[key]?.status === 'toDo'
    );
    // on le fait aussi car quand on passe en vue par shops ça affiche terminé puis en cours
    let effectiveStatus = statusTask.status;
    if (abortRequested && (statusTask.status === 'inProgress' || inProgress)) {
      effectiveStatus = 'aborted';
    }

    switch (effectiveStatus) {
      case 'inProgress':
        return (
          <Text component="span" fontWeight={400} color="pml">
            En cours
          </Text>
        );
      case 'error':
        return (
          <Text component="span" fontWeight={400} color="error">
            Erreur
          </Text>
        );
      case 'aborted':
        return (
          <Text component="span" fontWeight={400} color="insight">
            Avorté
          </Text>
        );
      case 'done':
        return (
          <Text
            component="span"
            fontWeight={400}
            color={inProgress ? 'pml' : 'activation'}
          >
            {inProgress ? 'En cours' : 'Terminé'}
          </Text>
        );
      default:
        return (
          <Text component="span" fontWeight={400} color="activation">
            {effectiveStatus}
          </Text>
        );
    }
  };

  const handleChangeNetwork = key => {
    setSelectedShopGroup(key);
  };

  const handleChangeImportType = key => {
    setImportType(key);
  };

  const renderImportTypeSelect = () => (
    <Row spacing={4}>
      <Item xs={4}>
        <Select
          title="Type d'import"
          value={importType}
          onChange={handleChangeImportType}
          options={importTypeOptions}
          small
        />
      </Item>
    </Row>
  );

  const onStop = async () => {
    if (!setupTask?.id) return;

    setAbortRequested(true);
    setComputing(false);

    await PML.stopSetupTask(setupTask.id);
    await getStatus();
  };

  if (!loaded) {
    return (
      <Container>
        <Loading loading />
      </Container>
    );
  }

  if (importType === 'repiquage') {
    return (
      <RepiquageImport
        renderImportTypeSelect={renderImportTypeSelect}
        onUnauthorized={() => setOpenModal('unauthorized')}
      />
    );
  }

  const getDurationLabel = task => {
    if (!task?.createdAt || !task?.updatedAt) {
      return '-';
    }
    const minutes = moment(task.updatedAt).diff(
      moment(task.createdAt),
      'minutes'
    );
    if (minutes === 0) {
      return 'moins d’une minute';
    }
    return `${minutes} ${pluralize('minute', minutes)}`;
  };

  const taskInProgress = isInProgress || shopIsInProgress;
  const showInProgressUI = taskInProgress && !abortRequested;

  return (
    <Container>
      {openModal === 'addShopGroup' && (
        <AddShopGroupModal
          handleClose={closeModal}
          onNext={datas => {
            createShopGroup(datas);
          }}
        />
      )}
      <IntervalTimer
        timeout={3000}
        callback={getStatus}
        enabled={!!config}
        repeat
      />
      {renderImportTypeSelect()}
      <Row>
        <Item>
          <Text fontWeight={700} fontSize="14px">
            Configuration plateforme multi-locale : {config.domain}
          </Text>
        </Item>
      </Row>
      <Row spacing={4}>
        <Item xs={4}>
          <Select
            title="Groupe de magasins"
            value={selectedShopGroup}
            onChange={handleChangeNetwork}
            options={shopGroupOptions}
            small
          />
        </Item>
        <Item xs={4}>
          <TextField
            title="URL du Google Sheet"
            value={config?.googleSheetUrl}
            onChange={value => {
              setConfig({
                ...config,
                googleSheetUrl: value
              });
            }}
          />
        </Item>
      </Row>
      <Row>
        <Item xs={4} justify="flex-start" style={{ paddingTop: '0px' }}>
          <Button
            onClick={onAdd}
            uppercase
            style={{
              textDecoration: 'underline'
            }}
          >
            Nouveau groupe de magasins
          </Button>
        </Item>
        <Item xs={4} />
      </Row>
      {(!setupTask || !isInProgress || !shopIsInProgress) && (
        <Row>
          <Item flex style={{ padding: '10px' }}>
            <Button
              onClick={onSave}
              variant="contained"
              color={
                original.googleSheetUrl === config.googleSheetUrl
                  ? themeColors.black
                  : undefined
              }
              disabled={
                original.googleSheetUrl === config.googleSheetUrl ||
                !config.googleSheetUrl ||
                googleSheetUrlError
              }
              style={{
                padding: '10px 16px'
              }}
            >
              <SaveIcon />
              &nbsp; Sauvegarder
            </Button>
          </Item>
          {!!config.id && (
            <Item flex style={{ padding: '10px' }}>
              <Button
                variant="contained"
                onClick={() => onCompute()}
                color={
                  original.googleSheetUrl !== config.googleSheetUrl
                    ? themeColors.black
                    : undefined
                }
                disabled={
                  !isAborted &&
                  (computing ||
                    taskInProgress ||
                    original.googleSheetUrl !== config.googleSheetUrl ||
                    !config.googleSheetUrl ||
                    googleSheetUrlError)
                }
                style={{
                  padding: '10px 16px'
                }}
              >
                <Container spacing={0}>
                  <Row spacing={0} alignItems="center">
                    <Item
                      flex
                      style={{
                        paddingRight: '10px'
                      }}
                    >
                      {computing ? (
                        <Loading
                          loading
                          width="24px"
                          color={themeColors.black}
                        />
                      ) : (
                        <FlightTakeoffIcon />
                      )}
                    </Item>
                    <Item xs>
                      <Text
                        fontSize="inherit"
                        fontWeight="inherit"
                        color="inherit"
                      >
                        Importer la configuration
                      </Text>
                    </Item>
                  </Row>
                </Container>
              </Button>
            </Item>
          )}
          {setupTask?.status === 'error' && setupTask.lastLine !== 0 && (
            <Item flex style={{ padding: '10px' }}>
              <Button
                variant="contained"
                onClick={() => onCompute(setupTask.lastLine)}
                loading={computing}
                color={
                  original.googleSheetUrl !== config.googleSheetUrl
                    ? themeColors.black
                    : undefined
                }
                disabled={
                  original.googleSheetUrl !== config.googleSheetUrl ||
                  !config.googleSheetUrl
                }
                style={{
                  padding: '10px 16px'
                }}
              >
                <Container spacing={0}>
                  <Row spacing={0} alignItems="center">
                    <Item
                      flex
                      style={{
                        paddingRight: '10px'
                      }}
                    >
                      {computing ? (
                        <Loading
                          loading
                          width="24px"
                          color={themeColors.keep_light}
                        />
                      ) : (
                        <ReplayIcon />
                      )}
                    </Item>
                    <Item xs>
                      <Text
                        fontSize="inherit"
                        fontWeight="inherit"
                        color="inherit"
                      >
                        Reprendre
                      </Text>
                    </Item>
                  </Row>
                </Container>
              </Button>
            </Item>
          )}
        </Row>
      )}
      {showInProgressUI && (
        <Row>
          <Item flex style={{ padding: '10px' }}>
            <Button
              onClick={onStop}
              variant="contained"
              color={themeColors.black}
              disabled={!taskInProgress}
              style={{
                padding: '10px 16px'
              }}
            >
              <CancelIcon />
              &nbsp; Avorter
            </Button>
          </Item>
        </Row>
      )}
      {setupTask && (
        <Row justify="center">
          <Item>
            <Text fontWeight={700}>
              {showInProgressUI
                ? "Le fichier est en cours d'import"
                : 'Historique de votre configuration :'}
            </Text>
          </Item>
          {showInProgressUI && shopIsInProgress && (
            <Item>
              <LinearProgress
                color={themeColors.pml}
                value={progress}
                variant="determinate"
              />
            </Item>
          )}
          <Item
            flex
            style={{
              margin: '50px',
              maxWidth: '450px'
            }}
          >
            <Row spacing={0}>
              <Item justify="flex-start">
                <Text fontWeight={700}>
                  Date d&apos;import :&nbsp;
                  <Text component="span" fontWeight={400}>
                    {moment(
                      showInProgressUI
                        ? setupTask.createdAt
                        : setupTask.updatedAt
                    ).format('DD/MM/YYYY HH:mm:ss')}
                  </Text>
                </Text>
              </Item>
              {showInProgressUI && (
                <Item justify="flex-start">
                  <Text fontWeight={700}>
                    En cours depuis :&nbsp;
                    <Text component="span" fontWeight={400}>
                      {getDurationLabel(setupTask)}
                    </Text>
                  </Text>
                </Item>
              )}
              <Item justify="flex-start">
                <Text fontWeight={700}>
                  Status :&nbsp;
                  {getStatusTxt(setupTask)}
                </Text>
              </Item>
              {showInProgressUI && setupTask.step && (
                <Item justify="flex-start">
                  <Row spacing={0}>
                    <Item flex>
                      <Text fontWeight={700}>Dernière étape: &nbsp;</Text>
                    </Item>
                    <Item xs justify="flex-start">
                      <Text
                        component="span"
                        fontWeight={400}
                        style={{
                          whiteSpace: 'pre-wrap'
                        }}
                      >
                        {setupTask.step}
                      </Text>
                    </Item>
                  </Row>
                </Item>
              )}
              {setupTask.message && (
                <Item justify="flex-start">
                  <Row spacing={0}>
                    <Item justify="flex-start">
                      <Text fontWeight={700}>
                        Message d&lsquo;erreur &nbsp;
                      </Text>
                    </Item>
                    <Item
                      justify="flex-start"
                      style={{
                        backgroundColor: themeColors.primary_header,
                        padding: '10px',
                        overflowWrap: 'anywhere'
                      }}
                    >
                      <Text color="keep_light" fontWeight={400} fontSize="10px">
                        {setupTask.message}
                      </Text>
                    </Item>
                  </Row>
                </Item>
              )}
            </Row>
          </Item>
          {rows.length > 0 && (
            <Row style={{ padding: '20px', marginBottom: '30px' }}>
              <TableWithColapse
                rows={rows}
                defaultSortKey="status"
                viewedRows={10}
                subRowsBackgroundColor={themeColors.themeV2.white}
                subRowBackgroundcolor={themeColors.themeV2.white}
                header={[
                  {
                    key: 'key',
                    label: 'Key',
                    sortable: true
                  },
                  {
                    key: 'status',
                    label: 'Status',
                    sortable: true,
                    format: (element, row) => {
                      return (
                        <Item
                          style={
                            row?.subRows?.length > 0
                              ? { paddingLeft: '40px' }
                              : {}
                          }
                        >
                          {element}
                        </Item>
                      );
                    }
                  }
                ]}
                subRowHeader={[
                  {
                    key: 'message',
                    label: 'Message',
                    sortable: true,
                    format: element => {
                      return (
                        <Item
                          style={{
                            padding: '20px',
                            margin: '5px',
                            borderRadius: '5px',
                            textAlign: 'left',
                            alignItems: 'center',
                            flexDirection: 'row',
                            backgroundColor: themeColors.themeV2.errorBg
                          }}
                        >
                          <Item xs={1}>
                            <ErrorRounded color="error" />
                          </Item>
                          <Item xs={11}>{element}</Item>
                        </Item>
                      );
                    }
                  }
                ]}
                handleCollapse={row => {
                  const updatedRows = rows.map(el => {
                    const newEl = el;
                    if (newEl.index === row.index) {
                      newEl.collapse = !el.collapse;
                    }
                    return newEl;
                  });
                  setRows(updatedRows);
                }}
              />
            </Row>
          )}
          {setupTask && setupTask.warnings?.length > 0 && (
            <Row
              style={{
                padding: '0px 40px'
              }}
            >
              <Accordion
                onExpandChange={onExpandChange}
                expanded={warningsExpanded}
                iconColor={themeColors.pml}
                rows={[
                  {
                    key: 'warnings',
                    label: (
                      <Row
                        alignItems="center"
                        justify="flex-start"
                        spacing={0}
                        style={{
                          padding: '0px 3px'
                        }}
                      >
                        <Item xs justify="flex-start" alignItems="center">
                          <WarningRoundedIcon />
                          <Text
                            noWrap
                            fontWeight={700}
                            style={{
                              paddingLeft: '7px'
                            }}
                          >
                            Avertissements ({setupTask.warnings.length})
                          </Text>
                        </Item>
                      </Row>
                    ),
                    items: setupTask.warnings.map(el => <Text>{el}</Text>)
                  }
                ]}
                underline
              />
            </Row>
          )}
        </Row>
      )}
      {openModal === 'unauthorized' && (
        <UnauthorizedModal open onClose={closeModal} />
      )}
    </Container>
  );
};

export default ConfigContainer;
